import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { StepLayout } from '../components/shell/StepLayout';
import { RecordButton } from '../components/RecordButton';
import { Card, Skeleton, SkeletonGroup, Spinner, Toast } from '../components/ui';
import type { RecorderErrorKind } from '../hooks/useVoiceRecorder';
import { MAX_OWN_TAKE_SECONDS } from '@app/shared';
import { useT } from '../i18n';
import { voiceApi } from '../lib/voice-client';

/** Spec 8.5: the approved text as a teleprompter, one take. */
export function OwnTakePage() {
  const t = useT();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const draftId = Number(useParams().draftId);
  const valid = Number.isInteger(draftId) && draftId > 0;
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const state = useQuery({
    queryKey: ['draft-voice', draftId],
    queryFn: () => voiceApi.getDraftVoice(draftId),
    enabled: valid,
    staleTime: 0,
  });
  if (!valid) return <Navigate to="/" replace />;
  // «Озвучка» with «Начитаю сам» open.
  const voiceScreen = `/drafts/${draftId}/voice?voice=own`;

  async function upload(blob: Blob) {
    setBusy(true);
    setNote(null);
    try {
      const res = await voiceApi.uploadOwnTake(draftId, blob);
      if (res.kind === 'recording') {
        setNote(t.voiceSetup.problems[res.problem]);
      } else if (res.kind === 'ok' || res.kind === 'mismatch') {
        await qc.invalidateQueries({ queryKey: ['draft-voice', draftId] });
        navigate(voiceScreen, { replace: true });
      } else if (res.kind === 'limit') {
        setNote(t.voiceover.ownLimit);
      } else {
        setNote(t.common.failed);
      }
    } catch {
      setNote(t.common.failed);
    } finally {
      setBusy(false);
    }
  }

  const onError = (kind: RecorderErrorKind) =>
    setNote(kind === 'permission' ? t.voiceSetup.permission : t.common.failed);

  return (
    <StepLayout title={t.voiceover.teleprompterTitle} back={voiceScreen}>
      <div className="flex flex-col">
      <p className="text-sm text-hint mb-3">{t.voiceover.teleprompterHint(MAX_OWN_TAKE_SECONDS)}</p>

      {state.isLoading && (
        <SkeletonGroup label={t.common.loading} className="mb-4">
          <Skeleton className="h-64" />
        </SkeletonGroup>
      )}
      {state.data && (
        <Card variant="surface" padding="none" className="p-5 mb-4 space-y-4">
          {state.data.blocks.map((b) => (
            <p key={b.id} className="text-lg leading-relaxed">
              {b.text}
            </p>
          ))}
        </Card>
      )}

      <div className="sticky bottom-0 bg-canvas/95 pt-3 pb-[max(12px,env(safe-area-inset-bottom))] space-y-2">
        {note && <Toast tone="warning">{note}</Toast>}
        {busy ? (
          <div className="flex items-center justify-center gap-2 py-3 text-sm text-hint">
            <Spinner size="sm" />
            {t.voiceover.uploading}
          </div>
        ) : (
          <RecordButton
            maxSeconds={MAX_OWN_TAKE_SECONDS}
            disabled={!state.data}
            onRecorded={(b) => void upload(b)}
            onError={onError}
          />
        )}
      </div>
      </div>
    </StepLayout>
  );
}
